import { DatePipe, NgIf, NgFor, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { FooterVersionSelectService } from '@app/core/services/footer-version-select.service';
import { RouterService } from '@app/core/services/router.service';
import { SidenavType } from '@app/core/sidenav-type';
import { ActiveBezugsberechtigterStore } from '@app/core/state/active-bezugsberechtigter';
import { ActiveFahrzeugStore } from '@app/core/state/active-fahrzeug';
import { ActiveGebaeudeStore } from '@app/core/state/active-gebaeude';
import { ActivePartnerStore } from '@app/core/state/active-partner';
import { ActivePersonStore } from '@app/core/state/active-person';
import { ActivePoliceStore } from '@app/core/state/active-police';
import { ActiveSchadenStore } from '@app/core/state/active-schaden';
import { ActiveVertreterStore } from '@app/core/state/active-vertreter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PersonResult, PolizzeResult, SchadenResult, Version } from '@pezi/entities';
import { ActiveBeitragszahlerStore } from '@app/core/state/active-beitragszahler/active-beitragszahler.store';
import { ActiveBetreuerStore } from '@app/core/state/active-betreuer/active-betreuer.store';
import { ActiveVersichertepersonStore } from '@app/core/state/active-versicherteperson/active-versicherteperson.store';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { WARTUNGSDIENST_PREFIX } from '../../app-routing.module';

@UntilDestroy()
@Component({
  selector:        'app-footer-version-select',
  templateUrl:     './footer-version-select.component.html',
  styleUrl:        './footer-version-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports:         [
    NgIf,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    NgFor,
    MatMenuItem,
    NgClass
  ]
})
export class FooterVersionSelectComponent implements OnInit {

  public footerVersion: FooterVersion;
  public nextVersion: PersonResult | SchadenResult | PolizzeResult | Version;
  public prevVersion: PersonResult | SchadenResult | PolizzeResult | Version;
  public aktuellIdx = 0;
  private datePipe = new DatePipe('de-AT');

  constructor(private footerVersionSelectService: FooterVersionSelectService, private cd: ChangeDetectorRef,
              public activePersonStore: ActivePersonStore,
              public activePoliceStore: ActivePoliceStore, public activeSchadenStore: ActiveSchadenStore,
              public activeGebaeudeStore: ActiveGebaeudeStore, public activeFahrzeugStore: ActiveFahrzeugStore,
              public activeBetreuerStore: ActiveBetreuerStore, public activeVertreterStore: ActiveVertreterStore,
              public activeBezugsberechtigterStore: ActiveBezugsberechtigterStore, public activeBeitragszahlerStore: ActiveBeitragszahlerStore,
              public activeVersichertepersonStore: ActiveVersichertepersonStore, public activePartnerStore: ActivePartnerStore,
              private routerService: RouterService, private router: Router, private snackBar: MatSnackBar) {
  }

  ngOnInit() {
    /**
     * Footerversion wird in global-sidenav befüllt, da an dieser Stelle alle nötigen Daten zusammengesammelt werden und
     * dadurch für den Footer kein großer Umbau benötigt wird. Doppelte Subscriptions werden dadurch auch eingespart.
     */
    this.footerVersionSelectService.currentVersions.pipe(untilDestroyed(this))
      .subscribe(v => {
        this.footerVersion = v;
        this.initNextPrevVersions();
        this.cd.markForCheck();
      });
  }

  public clickMenu(evt: MouseEvent, version: PersonResult | SchadenResult | PolizzeResult | Version): void {
    evt.stopPropagation();
    this.navigateToVersion(version);
  }

  public isAktuell(version: PersonResult | SchadenResult | PolizzeResult | Version): boolean {
    if (this.footerVersion.currentVersion) {
      if (this.footerVersion.type === SidenavType.POLIZZE || this.footerVersion.type === SidenavType.SCHADEN) {
        return (version as PolizzeResult | SchadenResult).id === (this.footerVersion.versionen[this.aktuellIdx] as PolizzeResult | SchadenResult).id;
      } else {
        return (version as PersonResult | Version).version === (this.footerVersion.versionen[this.aktuellIdx] as PersonResult | Version).version;
      }
    }
    return false;
  }

  public getVersionLabel(version: PersonResult | SchadenResult | PolizzeResult | Version, type: SidenavType): string {
    if (version) {
      switch (type) {
        case SidenavType.PERSON:
        case SidenavType.PARTNER:
        case SidenavType.BEZUGSBERECHTIGTER:
        case SidenavType.BEITRAGSZAHLER:
        case SidenavType.VERSPERSON:
          return 'V' + (100000 - (version as PersonResult).version) + '  ';
        case SidenavType.FAHRZEUG:
        case SidenavType.GEBAEUDE:
        case SidenavType.VERTRETER:
        case SidenavType.BETREUER:
          return 'V' + (100000 - (version as Version).version) + '  ';
        case SidenavType.POLIZZE:
        case SidenavType.SCHADEN:
          return '';
        default:
          return '';
      }
    }
    return '';
  }

  public getAktuellItem(): PersonResult | SchadenResult | PolizzeResult | Version {
    if (this.footerVersion.currentVersion) {
      switch (this.footerVersion.type) {
        case SidenavType.PERSON:
        case SidenavType.PARTNER:
        case SidenavType.BEZUGSBERECHTIGTER:
        case SidenavType.BEITRAGSZAHLER:
        case SidenavType.VERSPERSON:
          return (this.footerVersion.versionen as PersonResult[]).find(vers => vers.version === this.footerVersion.currentVersion);
        case SidenavType.FAHRZEUG:
        case SidenavType.GEBAEUDE:
        case SidenavType.VERTRETER:
        case SidenavType.BETREUER:
          return (this.footerVersion.versionen as Version[]).find(vers => vers.version === this.footerVersion.currentVersion);
        case SidenavType.POLIZZE:
          return (this.footerVersion.versionen as PolizzeResult[]).find(vers => vers.id === this.footerVersion.currentVersion);
        case SidenavType.SCHADEN:
          return (this.footerVersion.versionen as SchadenResult[]).find(vers => vers.id === this.footerVersion.currentVersion);
        default:
          return null;
      }
    } else {
      return this.footerVersion.versionen[0];
    }
  }

  public getIcon(version: PersonResult | SchadenResult | PolizzeResult | Version, type: SidenavType, aktuell: boolean): string {
    if (version) {
      switch (type) {
        case SidenavType.POLIZZE:
          return (version as PolizzeResult).ungueltig ? 'cancel' : (aktuell ? 'verified' : 'history');
        default:
          return null;
      }
    }
  }

  public getIconClass(version: PersonResult | SchadenResult | PolizzeResult | Version, type: SidenavType, aktuell: boolean): string {
    if (version) {
      switch (type) {
        case SidenavType.POLIZZE:
          return (version as PolizzeResult).ungueltig ? 'error-icon' : (aktuell ? 'success-icon' : null);
        default:
          return null;
      }
    }
  }

  public getLabel(version: PersonResult | SchadenResult | PolizzeResult | Version, type: SidenavType, longform?: boolean): string {
    if (version) {
      switch (type) {
        case SidenavType.PERSON:
        case SidenavType.PARTNER:
        case SidenavType.BEZUGSBERECHTIGTER:
        case SidenavType.BEITRAGSZAHLER:
        case SidenavType.VERSPERSON:
          return (version as PersonResult).gueltigAbDatum ? this.datePipe.transform((version as PersonResult).gueltigAbDatum, 'dd.MM.yyyy') : '';
        case SidenavType.FAHRZEUG:
        case SidenavType.GEBAEUDE:
          return (version as Version).gueltigAbDatum ? this.datePipe.transform((version as Version).gueltigAbDatum, 'dd.MM.yyyy') : '';
        case SidenavType.VERTRETER:
        case SidenavType.BETREUER:
          return (version as Version).bearbeitungsDatum ? this.datePipe.transform((version as Version).bearbeitungsDatum, 'dd.MM.yyyy') : '';
        case SidenavType.POLIZZE:
          if (longform) {
            return this.getLongformVersion(version);
          }
          return (version as PolizzeResult).gueltigAbDatum ? this.datePipe.transform((version as PolizzeResult).gueltigAbDatum, 'dd.MM.yyyy') : '';
        case SidenavType.SCHADEN:
          return (version as SchadenResult).bearbeitungsDatum ? this.datePipe.transform((version as SchadenResult).bearbeitungsDatum, 'dd.MM.yyyy') : '';
        default:
          return '';
      }
    }
    return 'Version ungültig';
  }

  public navigateToVersion(version: PersonResult | SchadenResult | PolizzeResult | Version): void {
    let versionId;
    // Do not allow Navigation inside Wartungsdienst
    if (this.router.url.indexOf(WARTUNGSDIENST_PREFIX) === -1) {
      if (this.footerVersion.type === SidenavType.POLIZZE || this.footerVersion.type === SidenavType.SCHADEN) {
        versionId = (version as PolizzeResult | SchadenResult).id;
      } else {
        versionId = (version as PersonResult | Version).version;
      }
      if (versionId) {
        switch (this.footerVersion.type) {
          case SidenavType.PERSON:
            this.onPersonVersionSelect(versionId);
            break;
          case SidenavType.BEITRAGSZAHLER:
            this.onBeitragszahlerVersionSelect(versionId);
            break;
          case SidenavType.VERSPERSON:
            this.onVerspersonVersionSelect(versionId);
            break;
          case SidenavType.BETREUER:
            this.onBetreuerVersionSelect(versionId);
            break;
          case SidenavType.VERTRETER:
            this.onVertreterVersionSelect(versionId);
            break;
          case SidenavType.BEZUGSBERECHTIGTER:
            this.onBBRVersionSelect(versionId);
            break;
          case SidenavType.POLIZZE:
          case SidenavType.VERTRAG:
          case SidenavType.POSITION:
            this.onPoliceVersionSelect(versionId);
            break;
          case SidenavType.SCHADEN:
            this.onSchadenVersionSelect(versionId);
            break;
          case SidenavType.FAHRZEUG:
            this.onFahrzeugVersionSelect(versionId);
            break;
          case SidenavType.GEBAEUDE:
            this.onGebaeudeVersionSelect(versionId);
            break;
          case SidenavType.PARTNER:
            this.onPartnerVersionSelect(versionId);
            break;
        }
      }
    } else {
      this.snackBar.open('Aktionen können nur auf der aktuellen Version ausgeführt werden!');
    }

  }

  public onPersonVersionSelect(version: number) {
    const currentPerson = this.activePersonStore.data.getValue().person;
    if (currentPerson && version !== currentPerson.version) {
      this.routerService.navigatePersonDetailVersion(currentPerson.id, version, this.footerVersion.currentSubPage);
    }
  }

  public onBeitragszahlerVersionSelect(version: number) {
    const currentPerson = this.activeBeitragszahlerStore.data.getValue().beitragszahler;
    if (currentPerson && version !== currentPerson.version) {
      this.routerService.navigateBeitragszahlerDetailVersion(currentPerson.id, version, this.footerVersion.currentSubPage);
    }
  }

  public onVerspersonVersionSelect(version: number) {
    const currentPerson = this.activeVersichertepersonStore.data.getValue().versicherteperson;
    if (currentPerson && version !== currentPerson.version) {
      this.routerService.navigateVersichertepersonDetailVersion(currentPerson.id, version, this.footerVersion.currentSubPage);
    }

  }

  public onBetreuerVersionSelect(version: number) {
    const currentPerson = this.activeBetreuerStore.data.getValue().betreuer.person;
    if (currentPerson && version !== currentPerson.version) {
      this.routerService.navigateBetreuerDetailVersion(currentPerson.id, version, this.footerVersion.currentSubPage);
    }

  }

  public onVertreterVersionSelect(version: number) {
    const currentPerson = this.activeVertreterStore.data.getValue().vertreter.person;
    if (currentPerson && version !== currentPerson.version) {
      this.routerService.navigateVertreterDetailVersion(currentPerson.id, version, this.footerVersion.currentSubPage);
    }

  }

  public onPoliceVersionSelect(polint: number) {
    const currentPolice = this.activePoliceStore.data.getValue().police;
    if (!currentPolice || polint !== currentPolice.id) {
      this.routerService.navigatePoliceDetail(polint, this.footerVersion.currentSubPage);
    }

  }

  public onGebaeudeVersionSelect(vers: number) {
    const currentGeb = this.activeGebaeudeStore.data.getValue().gebaeude;
    if (!currentGeb || vers !== currentGeb.version) {
      this.routerService.navigateGebaeudeDetailVersion(currentGeb.id, vers, this.footerVersion.currentSubPage);
    }

  }

  public onSchadenVersionSelect(schadenId: number) {
    const currentSchaden = this.activeSchadenStore.data.getValue().schaden;
    if (!currentSchaden || schadenId !== currentSchaden.id) {
      this.routerService.navigateSchadenDetail(schadenId, this.footerVersion.currentSubPage);
    }

  }

  public onFahrzeugVersionSelect(version: number) {
    const currentFz = this.activeFahrzeugStore.data.getValue().fahrzeug;
    if (currentFz && version !== currentFz.version) {
      this.routerService.navigateFahrzeugDetailVersion(currentFz.id, version, this.footerVersion.currentSubPage);
    }

  }

  public onBBRVersionSelect(version: number) {
    const currentBBR = this.activeBezugsberechtigterStore.data.getValue().bezugsberechtigter;
    if (currentBBR && version !== currentBBR.version) {
      this.routerService.navigateBezugsberechtigterDetailVersion(currentBBR.id, version, this.footerVersion.currentSubPage);
    }

  }

  public onPartnerVersionSelect(version: number) {
    const currentP = this.activePartnerStore.data.getValue().partner;
    if (currentP && version !== currentP.version) {
      this.routerService.navigatePartnerDetailVersion(currentP.id, version, this.footerVersion.currentSubPage);
    }

  }

  private initNextPrevVersions(): void {
    this.prevVersion = null;
    this.nextVersion = null;
    this.aktuellIdx = 0;
    if (this.footerVersion && this.footerVersion.versionen && this.footerVersion.versionen.length > 1) {

      if (this.footerVersion.currentVersion) {
        if (this.footerVersion.type === SidenavType.POLIZZE || this.footerVersion.type === SidenavType.SCHADEN) {
          this.aktuellIdx = this.footerVersion.versionen.findIndex(vers => (vers as PolizzeResult | SchadenResult).id === this.footerVersion.currentVersion);
        } else {
          this.aktuellIdx = this.footerVersion.versionen.findIndex(vers => (vers as PersonResult | Version).version === this.footerVersion.currentVersion);
        }
      }
      if (this.aktuellIdx < this.footerVersion.versionen.length) {
        this.prevVersion = this.footerVersion.versionen[this.aktuellIdx + 1];
      }
      if (this.aktuellIdx > 0) {
        this.nextVersion = this.footerVersion.versionen[this.aktuellIdx - 1];
      }
    }
  }

  private getLongformVersion(version: PersonResult | SchadenResult | PolizzeResult | Version): string {
    return ((version as PolizzeResult).gueltigAbDatum ? this.datePipe.transform((version as PolizzeResult).gueltigAbDatum) : '-') + ' / ' +
      ((version as PolizzeResult).gueltigBisDatum ? this.datePipe.transform((version as PolizzeResult).gueltigBisDatum) : '-') + ' / ' +
      ((version as PolizzeResult).vke ? (version as PolizzeResult).vke.key : '-') + ' / ' +
      ((version as PolizzeResult).bearbeitungsArt ? ((version as PolizzeResult).bearbeitungsArt.key + ' ' + (version as PolizzeResult).bearbeitungsArt.value) : '-');
  }

}

export interface FooterVersion {
  versionen: PersonResult[] | SchadenResult[] | PolizzeResult[] | Version[];
  type: SidenavType;
  currentVersion?: number; // Version aktuell wenn nicht befüllt
  currentSubPage?: string;
}
